/**
 * Utility functions for managing saved jobs in localStorage
 */

import { getUserId } from './auth';

const STORAGE_KEY_PREFIX = 'savedJobs';
const LEGACY_STORAGE_KEY = 'savedJobs';
const GUEST_STORAGE_KEY = `${STORAGE_KEY_PREFIX}:guest`;

const getStorageKey = () => {
  const userId = getUserId();
  if (userId) {
    return `${STORAGE_KEY_PREFIX}:user:${userId}`;
  }
  return GUEST_STORAGE_KEY;
};

const isSameJobId = (left, right) => String(left) === String(right);

const parseSavedJobs = (rawValue) => {
  if (!rawValue) return [];
  const parsed = JSON.parse(rawValue);
  return Array.isArray(parsed) ? parsed : [];
};

const migrateLegacySavedJobs = () => {
  const scopedKey = getStorageKey();
  const scopedValue = localStorage.getItem(scopedKey);
  if (scopedValue !== null) return;

  const legacyValue = localStorage.getItem(LEGACY_STORAGE_KEY);
  if (!legacyValue) return;

  const legacyJobs = parseSavedJobs(legacyValue);
  localStorage.setItem(scopedKey, JSON.stringify(legacyJobs));
  localStorage.removeItem(LEGACY_STORAGE_KEY);
};

/**
 * Get all saved jobs from localStorage
 * @returns {Array} Array of saved job objects
 */
export function getSavedJobs() {
  try {
    migrateLegacySavedJobs();
    const saved = localStorage.getItem(getStorageKey());
    return parseSavedJobs(saved);
  } catch (error) {
    console.error('Error reading saved jobs:', error);
    return [];
  }
}

/**
 * Check if a job is saved
 * @param {string} jobId - The job ID to check
 * @returns {boolean} True if job is saved
 */
export function isJobSaved(jobId) {
  const savedJobs = getSavedJobs();
  return savedJobs.some((job) => isSameJobId(job.id, jobId));
}

/**
 * Save a job to localStorage
 * @param {Object} job - Job object to save
 * @param {string} job.id - Unique job ID
 * @param {string} job.title - Job title
 * @param {string} job.company - Company name
 * @param {string} job.location - Job location
 * @param {string} job.salary - Salary information
 * @param {string} job.description - Job description
 * @param {string} job.job_id - External job ID for interview
 * @returns {boolean} True if successfully saved
 */
export function saveJob(job) {
  try {
    const savedJobs = getSavedJobs();

    // Check if already saved
    if (savedJobs.some((saved) => isSameJobId(saved.id, job.id))) {
      return false;
    }

    // Add savedAt timestamp
    const jobToSave = {
      ...job,
      savedAt: new Date().toISOString(),
    };

    savedJobs.unshift(jobToSave); // Add to beginning
    localStorage.setItem(getStorageKey(), JSON.stringify(savedJobs));
    return true;
  } catch (error) {
    console.error('Error saving job:', error);
    return false;
  }
}

/**
 * Remove a saved job from localStorage
 * @param {string} jobId - The job ID to remove
 * @returns {boolean} True if successfully removed
 */
export function unsaveJob(jobId) {
  try {
    const savedJobs = getSavedJobs();
    const filtered = savedJobs.filter((job) => !isSameJobId(job.id, jobId));

    if (filtered.length === savedJobs.length) {
      return false; // Job was not found
    }

    localStorage.setItem(getStorageKey(), JSON.stringify(filtered));
    return true;
  } catch (error) {
    console.error('Error removing saved job:', error);
    return false;
  }
}

/**
 * Toggle job saved state
 * @param {Object} job - Job object
 * @returns {boolean} True if job is now saved, false if unsaved
 */
export function toggleSaveJob(job) {
  if (isJobSaved(job.id)) {
    unsaveJob(job.id);
    return false;
  } else {
    saveJob(job);
    return true;
  }
}

/**
 * Get count of saved jobs
 * @returns {number} Number of saved jobs
 */
export function getSavedJobsCount() {
  return getSavedJobs().length;
}
